'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface BlessingFormData {
  name: string;
  relationship: string;
  message: string;
  allowDisplay: boolean;
}

const BlessingForm = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState<BlessingFormData>({
    name: '',
    relationship: '',
    message: '',
    allowDisplay: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [errors, setErrors] = useState<Partial<BlessingFormData>>({});

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-lg p-8 shadow-sacred-md">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-surface rounded w-3/4" />
          <div className="h-10 bg-surface rounded" />
          <div className="h-10 bg-surface rounded" />
          <div className="h-32 bg-surface rounded" />
        </div>
      </div>
    );
  }

  const validateForm = (): boolean => {
    const newErrors: Partial<BlessingFormData> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.relationship.trim()) {
      newErrors.relationship = 'Relationship is required';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Blessing message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitSuccess(true);
      setFormData({
        name: '',
        relationship: '',
        message: '',
        allowDisplay: false
      });
      
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    }, 1500);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors[name as keyof BlessingFormData]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-background">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 mb-6 bg-accent/10 rounded-full">
            <Icon name="PencilSquareIcon" size={32} className="text-accent" variant="solid" />
          </div>
          <h2 className="font-playfair text-3xl sm:text-4xl font-bold text-foreground mb-4">
            Share Your Blessing
          </h2>
          <p className="font-inter text-lg text-muted-foreground">
            Your words of encouragement and prayers mean the world to us
          </p>
        </div>

        {submitSuccess && (
          <div className="mb-8 p-4 bg-success/10 border border-success/20 rounded-lg flex items-start space-x-3">
            <Icon name="CheckCircleIcon" size={24} className="text-success flex-shrink-0 mt-0.5" variant="solid" />
            <div>
              <p className="font-inter text-sm font-medium text-success">
                Thank you for your blessing!
              </p>
              <p className="font-inter text-xs text-success/80 mt-1">
                Your message has been received and will be cherished by Moyak and Gloria.
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-card rounded-lg p-8 shadow-sacred-md">
          <div className="space-y-6">
            <div>
              <label htmlFor="name" className="block font-inter text-sm font-medium text-foreground mb-2">
                Your Name <span className="text-error">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`w-full px-4 py-3 bg-input border ${errors.name ? 'border-error' : 'border-border'} rounded-lg font-inter text-sm text-foreground placeholder-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200`}
                placeholder="Enter your full name"
              />
              {errors.name && (
                <p className="mt-1 font-inter text-xs text-error">{errors.name}</p>
              )}
            </div>

            <div>
              <label htmlFor="relationship" className="block font-inter text-sm font-medium text-foreground mb-2">
                Your Relationship <span className="text-error">*</span>
              </label>
              <select
                id="relationship"
                name="relationship"
                value={formData.relationship}
                onChange={handleChange}
                className={`w-full px-4 py-3 bg-input border ${errors.relationship ? 'border-error' : 'border-border'} rounded-lg font-inter text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200`}
              >
                <option value="">Select your relationship</option>
                <option value="Family Member">Family Member</option>
                <option value="Close Friend">Close Friend</option>
                <option value="Church Member">Church Member</option>
                <option value="Colleague">Colleague</option>
                <option value="Community Member">Community Member</option>
                <option value="Other">Other</option>
              </select>
              {errors.relationship && (
                <p className="mt-1 font-inter text-xs text-error">{errors.relationship}</p>
              )}
            </div>

            <div>
              <label htmlFor="message" className="block font-inter text-sm font-medium text-foreground mb-2">
                Your Blessing Message <span className="text-error">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={6}
                className={`w-full px-4 py-3 bg-input border ${errors.message ? 'border-error' : 'border-border'} rounded-lg font-inter text-sm text-foreground placeholder-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 resize-none`}
                placeholder="Share your prayers, blessings, or words of wisdom for Moyak and Gloria..."
              />
              {errors.message && (
                <p className="mt-1 font-inter text-xs text-error">{errors.message}</p>
              )}
              <p className="mt-2 font-inter text-xs text-muted">
                Minimum 10 characters
              </p>
            </div>

            <div className="flex items-start space-x-3">
              <input
                type="checkbox"
                id="allowDisplay"
                name="allowDisplay"
                checked={formData.allowDisplay}
                onChange={handleChange}
                className="mt-1 w-4 h-4 text-primary bg-input border-border rounded focus:ring-2 focus:ring-primary"
              />
              <label htmlFor="allowDisplay" className="font-inter text-sm text-muted-foreground">
                I give permission for my blessing to be displayed on the website (your message may be shared with other guests)
              </label>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-6 py-4 bg-primary text-primary-foreground font-inter text-sm font-medium rounded-lg hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <Icon name="PaperAirplaneIcon" size={20} />
                  <span>Send Blessing</span>
                </>
              )}
            </button>
          </div>
        </form>

        <div className="mt-8 p-6 bg-surface rounded-lg border border-border">
          <div className="flex items-start space-x-3">
            <Icon name="InformationCircleIcon" size={24} className="text-primary flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-inter text-sm text-foreground mb-2">
                <strong>Privacy Notice:</strong> Your blessing message will be received by Moyak and Gloria. If you grant permission, selected messages may be displayed on this website to inspire and encourage other guests.
              </p>
              <p className="font-inter text-xs text-muted">
                All submissions are reviewed before being displayed publicly.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BlessingForm;